import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { siteConfigService } from '../../services/siteConfig';
import type { SiteConfig } from '../../services/siteConfig';
import Navbar from '../../components/feature/Navbar';
import LoadingSpinner from '../../components/feature/LoadingSpinner';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export default function Admin() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'config' | 'messages'>('config');
  const [config, setConfig] = useState<SiteConfig>({
    show_title: true,
    site_title: 'VINTAGE LITERAL',
    logo_url: '',
    logo_width: 40,
    logo_height: 40,
    hero_image_url: '',
    hero_image_scale: 'cover',
    hero_image_position: 'left-top',
    hero_title: 'VINTAGE LITERAL',
    hero_subtitle: 'Explora la literatura\ncon mirada retro',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Contact messages state
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/');
      return;
    }
    loadConfig();
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin' && activeTab === 'messages') {
      loadMessages();
    }
  }, [activeTab, isAuthenticated, user]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const siteConfig = await siteConfigService.getSiteConfig();
      setConfig(siteConfig);
    } catch (error) {
      console.error('Error loading config:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración' });
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      setMessagesLoading(true);
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/contact', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar mensajes');
      const data = await res.json();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessage({ type: 'error', text: 'Error al cargar mensajes' });
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`/api/contact/${id}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al marcar mensaje');
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (!confirm('¿Eliminar este mensaje permanentemente?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`/api/contact/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar mensaje');
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await siteConfigService.updateSiteConfig(config);
      setMessage({ type: 'success', text: 'Configuración guardada exitosamente' });
      setTimeout(async () => {
        try {
          const updatedConfig = await siteConfigService.getSiteConfig();
          setConfig(updatedConfig);
        } catch (error) {
          console.error('Error reloading config:', error);
        }
      }, 1000);
    } catch (error: any) {
      console.error('Error saving config:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Error al guardar la configuración'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key: keyof SiteConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
    if (message) setMessage(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20">
          <LoadingSpinner message="Verificando acceso..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sepia-600 hover:text-sepia-800 font-ui text-sm mb-4 transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al inicio
          </button>
          
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <h1 className="font-headings text-xl text-sepia-900 mb-2">PANEL DE ADMINISTRACIÓN</h1>
            <p className="text-sm text-sepia-600 font-ui">
              {activeTab === 'config' ? 'Configura la apariencia del sitio web' : 'Gestiona los mensajes de contacto'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 text-sm font-ui rounded-[10%] transition-all duration-200 ${
              activeTab === 'config'
                ? 'vintage-paper text-sepia-900 shadow-sm'
                : 'vintage-paper-subtle text-sepia-600 hover:text-sepia-800'
            }`}
          >
            <i className="ri-settings-line mr-1"></i>
            Configuración
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-4 py-2 text-sm font-ui rounded-[10%] transition-all duration-200 ${
              activeTab === 'messages'
                ? 'vintage-paper text-sepia-900 shadow-sm'
                : 'vintage-paper-subtle text-sepia-600 hover:text-sepia-800'
            }`}
          >
            <i className="ri-mail-line mr-1"></i>
            Mensajes de contacto
            {messages.filter(m => !m.is_read).length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {messages.filter(m => !m.is_read).length}
              </span>
            )}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`vintage-paper aged-paper p-4 shadow-lg mb-6 border-l-4 ${
            message.type === 'success' ? 'border-green-500' : 'border-red-500'
          }`}>
            <div className="flex items-center gap-2">
              <i className={`${
                message.type === 'success' ? 'ri-check-line text-green-600' : 'ri-error-warning-line text-red-600'
              }`}></i>
              <p className={`text-sm font-ui ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <>
            {loading ? (
              <LoadingSpinner message="Cargando configuración..." />
            ) : (
              <>
                {/* Hero Configuration */}
                <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
                  <h2 className="font-headings text-lg text-sepia-900 mb-4 flex items-center gap-2">
                    <i className="ri-landscape-line text-sepia-600"></i>
                    CONFIGURACIÓN DEL HERO
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-ui text-sepia-700 mb-2">
                        Título del Hero
                      </label>
                      <input
                        type="text"
                        value={config.hero_title}
                        onChange={(e) => handleInputChange('hero_title', e.target.value)}
                        placeholder="VINTAGE LITERAL"
                        className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-ui text-sepia-700 mb-2">
                        Subtítulo del Hero
                      </label>
                      <textarea
                        value={config.hero_subtitle}
                        onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                        placeholder="Explora la literatura&#10;con mirada retro"
                        rows={3}
                        className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 resize-none"
                      />
                      <p className="text-xs text-sepia-500 font-ui mt-1">
                        Usa saltos de línea para separar el texto en múltiples líneas
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-ui text-sepia-700 mb-2">
                        URL de la Imagen Hero
                      </label>
                      <input
                        type="url"
                        value={config.hero_image_url}
                        onChange={(e) => handleInputChange('hero_image_url', e.target.value)}
                        placeholder="https://ejemplo.com/imagen-hero.jpg"
                        className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                      />
                      <p className="text-xs text-sepia-500 font-ui mt-1">
                        Deja vacío para usar la imagen por defecto
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-ui text-sepia-700 mb-2">
                          Escalado de Imagen
                        </label>
                        <select
                          value={config.hero_image_scale}
                          onChange={(e) => handleInputChange('hero_image_scale', e.target.value)}
                          className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 appearance-none bg-white"
                        >
                          <option value="cover">Cubrir (cover)</option>
                          <option value="contain">Contener (contain)</option>
                          <option value="fill">Rellenar (fill)</option>
                        </select>
                        <p className="text-xs text-sepia-500 font-ui mt-1">
                          Cover: Llena el área, puede recortar<br/>
                          Contain: Imagen completa visible<br/>
                          Fill: Estira para llenar exacto
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-ui text-sepia-700 mb-2">
                          Posición de Imagen
                        </label>
                        <select
                          value={config.hero_image_position}
                          onChange={(e) => handleInputChange('hero_image_position', e.target.value)}
                          className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 appearance-none bg-white"
                        >
                          <option value="center">Centro</option>
                          <option value="top">Arriba</option>
                          <option value="bottom">Abajo</option>
                          <option value="left">Izquierda</option>
                          <option value="right">Derecha</option>
                          <option value="left-top">Arriba Izquierda</option>
                          <option value="right-top">Arriba Derecha</option>
                          <option value="left-bottom">Abajo Izquierda</option>
                          <option value="right-bottom">Abajo Derecha</option>
                        </select>
                      </div>
                    </div>

                    {/* Hero Preview */}
                    <div className="vintage-paper-subtle p-4 rounded-[10%]">
                      <p className="text-sm font-ui text-sepia-700 mb-2">Vista previa del hero:</p>
                      <div className="relative h-32 overflow-hidden rounded-[10%] border border-sepia-300/30">
                        <img
                          src={config.hero_image_url || "https://readdy.ai/api/search-image?query=vintage%20library%20scene%20with%20antique%20typewriter%2C%20old%20wooden%20desk%20with%20brass%20details%2C%20leather-bound%20books%20stacked%20around%2C%20warm%20sepia%20lighting%2C%20nostalgic%20atmosphere%2C%20classic%20literature%20setting%2C%20aged%20paper%20manuscripts%2C%20fountain%20pen%20nearby%2C%20mahogany%20bookshelf%20background%2C%20sepia%20and%20brown%20tones%2C%20literary%20workspace%20aesthetic%2C%20dusty%20sunbeams%20through%20window%2C%20scholarly%20ambiance&width=375&height=256&seq=hero_vintage_005&orientation=landscape"}
                          alt="Vista previa imagen hero"
                          className={`w-full h-full object-${config.hero_image_scale} object-${config.hero_image_position.replace('-', '-')}`}
                          onError={(e) => {
                            e.currentTarget.src = "https://readdy.ai/api/search-image?query=vintage%20library%20scene%20with%20antique%20typewriter%2C%20old%20wooden%20desk%20with%20brass%20details%2C%20leather-bound%20books%20stacked%20around%2C%20warm%20sepia%20lighting%2C%20nostalgic%20atmosphere%2C%20classic%20literature%20setting%2C%20aged%20paper%20manuscripts%2C%20fountain%20pen%20nearby%2C%20mahogany%20bookshelf%20background%2C%20sepia%20and%20brown%20tones%2C%20literary%20workspace%20aesthetic%2C%20dusty%20sunbeams%20through%20window%2C%20scholarly%20ambiance&width=375&height=256&seq=hero_vintage_005&orientation=landscape";
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-sepia-900/70 via-sepia-800/60 to-sepia-900/80"></div>
                        <div className="absolute inset-0 bg-sepia-900/40"></div>
                        <div className="absolute inset-0 bg-sepia-800/30 mix-blend-multiply"></div>
                        <div className="absolute inset-0 flex flex-col items-center justify-center px-2">
                          <h3 className="text-sm font-headings font-bold text-sepia-100 mb-1 text-center tracking-wide" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.9), 1px 1px 4px rgba(0,0,0,0.7)' }}>
                            {config.hero_title}
                          </h3>
                          <p className="text-xs text-sepia-200 text-center opacity-95 leading-relaxed whitespace-pre-line" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                            {config.hero_subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logo Configuration */}
                <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
                  <h2 className="font-headings text-lg text-sepia-900 mb-4 flex items-center gap-2">
                    <i className="ri-image-line text-sepia-600"></i>
                    CONFIGURACIÓN DE LOGO
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-ui text-sepia-700 mb-2">
                        URL del Logo
                      </label>
                      <input
                        type="url"
                        value={config.logo_url}
                        onChange={(e) => handleInputChange('logo_url', e.target.value)}
                        placeholder="https://ejemplo.com/logo.png"
                        className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                      />
                      <p className="text-xs text-sepia-500 font-ui mt-1">
                        Deja vacío para no mostrar logo
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-ui text-sepia-700 mb-2">
                          Ancho (px)
                        </label>
                        <input
                          type="number"
                          value={config.logo_width}
                          onChange={(e) => handleInputChange('logo_width', parseInt(e.target.value) || 40)}
                          min="20"
                          max="200"
                          className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-ui text-sepia-700 mb-2">
                          Alto (px)
                        </label>
                        <input
                          type="number"
                          value={config.logo_height}
                          onChange={(e) => handleInputChange('logo_height', parseInt(e.target.value) || 40)}
                          min="20"
                          max="200"
                          className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                        />
                      </div>
                    </div>

                    {config.logo_url && (
                      <div className="vintage-paper-subtle p-4 rounded-[10%]">
                        <p className="text-sm font-ui text-sepia-700 mb-2">Vista previa:</p>
                        <img
                          src={config.logo_url}
                          alt="Logo preview"
                          width={config.logo_width}
                          height={config.logo_height}
                          className="object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Title Configuration */}
                <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
                  <h2 className="font-headings text-lg text-sepia-900 mb-4 flex items-center gap-2">
                    <i className="ri-text text-sepia-600"></i>
                    CONFIGURACIÓN DE TÍTULO
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="show_title"
                        checked={config.show_title}
                        onChange={(e) => handleInputChange('show_title', e.target.checked)}
                        className="w-4 h-4 text-sepia-600 vintage-paper-subtle border border-sepia-300/50 rounded focus:ring-sepia-500/50"
                      />
                      <label htmlFor="show_title" className="text-sm font-ui text-sepia-700">
                        Mostrar título del sitio
                      </label>
                    </div>

                    {config.show_title && (
                      <div>
                        <label className="block text-sm font-ui text-sepia-700 mb-2">
                          Título del Sitio
                        </label>
                        <input
                          type="text"
                          value={config.site_title}
                          onChange={(e) => handleInputChange('site_title', e.target.value)}
                          placeholder="VINTAGE LITERAL"
                          className="w-full px-3 py-2 text-sm font-ui text-sepia-800 vintage-paper-subtle border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview */}
                <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
                  <h2 className="font-headings text-lg text-sepia-900 mb-4 flex items-center gap-2">
                    <i className="ri-eye-line text-sepia-600"></i>
                    VISTA PREVIA DEL NAVBAR
                  </h2>
                  
                  <div className="vintage-paper-subtle p-4 rounded-[10%] border border-sepia-300/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {config.logo_url && (
                          <img
                            src={config.logo_url}
                            alt="Logo"
                            width={config.logo_width}
                            height={config.logo_height}
                            className="object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        {config.show_title && (
                          <span className="font-headings text-lg font-bold text-sepia-900">
                            {config.site_title}
                          </span>
                        )}
                        {!config.logo_url && !config.show_title && (
                          <span className="text-sm text-sepia-500 font-ui italic">
                            (Sin logo ni título)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-sepia-200/60 rounded-full"></div>
                        <div className="w-6 h-6 bg-sepia-200/60 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="text-center">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-6 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line"></i>
                        Guardar Configuración
                      </>
                    )}
                  </button>
                  
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-sepia-600 font-ui text-center">
                      Los cambios se guardan automáticamente en la base de datos
                    </p>
                    {message?.type === 'success' && (
                      <button
                        onClick={() => window.location.reload()}
                        className="vintage-paper-subtle text-sepia-700 hover:bg-sepia-100/70 px-4 py-2 text-xs font-ui transition-all duration-300 rounded-[10%] hover:shadow-sm flex items-center justify-center gap-2 mx-auto"
                      >
                        <i className="ri-refresh-line"></i>
                        Ver cambios aplicados
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-headings text-lg text-sepia-900 flex items-center gap-2">
                <i className="ri-mail-open-line text-sepia-600"></i>
                MENSAJES DE CONTACTO
              </h2>
              <button
                onClick={loadMessages}
                disabled={messagesLoading}
                className="vintage-paper-subtle text-sepia-700 hover:bg-sepia-200/70 px-3 py-1.5 text-xs font-ui transition-all duration-300 rounded-[10%] flex items-center gap-1"
              >
                <i className="ri-refresh-line"></i>
                Refrescar
              </button>
            </div>

            {messagesLoading ? (
              <LoadingSpinner message="Cargando mensajes..." />
            ) : messages.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-inbox-line text-4xl text-sepia-400 mb-3 block"></i>
                <p className="text-sm text-sepia-600 font-ui">No hay mensajes de contacto</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`vintage-paper-subtle p-4 rounded-[10%] border transition-all duration-200 ${
                      msg.is_read ? 'border-sepia-200/30' : 'border-sepia-400/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-ui text-sm font-semibold text-sepia-900 truncate">
                            {msg.name}
                          </span>
                          {!msg.is_read && (
                            <span className="bg-sepia-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-ui">
                              Nuevo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-sepia-600 font-ui truncate">{msg.email}</p>
                        {msg.subject && (
                          <p className="text-xs text-sepia-700 font-ui mt-0.5">
                            Asunto: {msg.subject}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {!msg.is_read && (
                          <button
                            onClick={() => handleMarkRead(msg.id)}
                            className="p-1.5 text-sepia-600 hover:text-sepia-800 hover:bg-sepia-200/50 rounded-[10%] transition-colors"
                            title="Marcar como leído"
                          >
                            <i className="ri-check-double-line text-sm"></i>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100/50 rounded-[10%] transition-colors"
                          title="Eliminar mensaje"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                      className="w-full text-left"
                    >
                      <p className={`text-xs text-sepia-800 font-body leading-relaxed ${
                        expandedId !== msg.id ? 'line-clamp-2' : ''
                      }`}>
                        {msg.message}
                      </p>
                    </button>

                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-sepia-500 font-ui">
                        {new Date(msg.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {expandedId === msg.id ? (
                        <span className="text-[10px] text-sepia-600 font-ui">Mostrar menos</span>
                      ) : (
                        <span className="text-[10px] text-sepia-600 font-ui">Ver mensaje completo</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vintage Background */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20administrative%20office%2C%20antique%20wooden%20desk%20with%20brass%20details%2C%20old%20filing%20cabinets%2C%20classic%20typewriter%2C%20administrative%20documents%2C%20warm%20sepia%20lighting%2C%20scholarly%20atmosphere%2C%20aged%20paper%20and%20parchment%2C%20brown%20and%20cream%20tones%2C%20nostalgic%20office%20setting%2C%20vintage%20stamps%20and%20seals&width=375&height=812&seq=admin_bg&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
